import { useCampaignFlags, trackCampaignEvent, campaignFlagsState } from './campaignFlags'
import { isPromoFreeRoute } from './campaignSuppress'

export default function createCampaignElementMixin(elementName) {
	return {
		props: {
			// Force this element visible regardless of the admin toggle, embed
			// route, or session suppression — used by the admin `/campaign-preview`
			// page so staff can eyeball every element's artwork. Tracking is
			// suppressed in preview mode so the funnel stats stay clean.
			preview: { type: Boolean, default: false },
		},
		data() {
			// Plain reactive component data — Vue tracks `this.campaignEnabled`
			// in v-if without needing the module-level reactive proxy to be a
			// dependency of the render-effect. Both a `setup()`-returned computed
			// ref and an options-API computed-via-mixin failed to re-track the
			// dependency on `state.flags` once the flag map was replaced, so
			// toggling in admin never propagated to v-if. A `$watch` on the
			// module-level state, mirrored into this local data property, is the
			// reliable path.
			//
			// Starts hidden and is resolved synchronously in created() below. Defaulting to
			// visible meant an element whose flag lookup failed rendered anyway and could not
			// be switched off at all (HAP-3654).
			return { campaignEnabled: false }
		},
		computed: {
			campaignElementName() {
				return elementName
			},
		},
		created() {
			const api = useCampaignFlags()
			this.campaignFlagsApi = api
			const update = () => {
				// Preview mode (admin artwork preview) always shows, bypassing the
				// admin toggle, embed suppression, and flag state.
				if (this.preview) {
					this.campaignEnabled = true
					return
				}
				// Never show campaign/promo content on embed pages (they live on
				// partner churches' own sites) or on the other promo-free routes.
				if (isPromoFreeRoute(this.$route?.path)) {
					this.campaignEnabled = false
					return
				}
				this.campaignEnabled = api ? api.isEnabled(elementName) : false
			}
			update()
			// Watch the module-level state object deeply so we react to both a
			// `state.flags = {...}` replacement and individual key mutations.
			this.$_campaignUnwatch = this.$watch(
				() => campaignFlagsState.flags,
				update,
				{ deep: true, immediate: false },
			)
			// Re-evaluate when navigating into/out of an embed route within the SPA.
			this.$_campaignRouteUnwatch = this.$watch(() => this.$route?.path, update)
		},
		beforeUnmount() {
			if (typeof this.$_campaignUnwatch === 'function') this.$_campaignUnwatch()
			if (typeof this.$_campaignRouteUnwatch === 'function') this.$_campaignRouteUnwatch()
		},
		methods: {
			trackCampaignImpression(meta) {
				if (this.preview) return
				trackCampaignEvent({ element: elementName, type: 'impression', route: this.$route?.path, meta })
			},
			trackCampaignClick(meta) {
				if (this.preview) return
				trackCampaignEvent({ element: elementName, type: 'click', route: this.$route?.path, meta })
			},
			trackCampaignDismiss(meta) {
				if (this.preview) return
				trackCampaignEvent({ element: elementName, type: 'dismiss', route: this.$route?.path, meta })
			},
		},
	}
}
